// TODO: keep track that focus stays on the same place during PLP update

import { TCart } from 'widgets/cart/CartMgr';
import { TGTMInfo } from 'types/models/product';
import { TProduct as TBaseProduct } from './CoreProductDetail';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TDeliveryMethodData } from './ProductDeliveryOptions';
import { TAddToCartButtonInstance } from './AddToCartButton';
import { TWidget } from 'widgets/Widget';
import { isMember } from 'core/utils';

export type TAddToCartServerResponse = TServerResponse & {
    cart: TCart & {
        addedBonusProductsGtmInfo: Array<TGTMInfo>;
        addedProducts?: Array<{ gtmInfo?: { item_id?: string } }>;
        addedProduct?: { gtmInfo?: { item_id?: string } };
    };
};

type TProcessButtonPrefs = ReturnType<TProcessButtonInstance['prefs']>;

export interface IAddToCartButton extends TProcessButtonInstance {
    prefs(): TProcessButtonPrefs & {
        isOutOfStock?: boolean;
        stock: string;
    };
}

type TAddProductRequestData = {
    pid: string;
    quantity: number;
    options?: string;
    childProducts?: string;
    storeId?: string;
    pageContext?: string;
};

export type TProduct = TBaseProduct & {
    attributeSelections: { [key: string]: boolean };
};

type TActionButtonType = 'addToCart' | 'backInStock' | 'signInToBuy';

const RECOMMENDATIONS_SLOT_SELECTOR = '.l-pdp-slots_first';

/**
 * @description  Core AddToCartMixin implementation - changed emit to trigger drawer instead of modal
 * <br>This class is not intended to have a separate DOM representation.
 * @param AddToCartMixin widget for extending
 * @returns Add to Cart mixin widget
 */
function CoreAddToCartMixinExtClassCreator(AddToCartMixin: import('widgets/product/AddToCartMixin').TAddToCartMixin) {
    /**
     * @class CoreAddToCartMixin
     * @augments AddToCartMixin
     * @property {string} data-text-network-error - network error text
     * @property {string} data-add-to-cart-label - add to cart label
     * @property {string} data-pid - product id
     * @property {boolean} data-show-minicart-on-product-add - Show minicart on product added to cart
     * @property {boolean} data-show-message-on-product-add - Show message on product added to cart
     * @property {boolean} data-show-alert-on-product-add - Show Global Alert on product added to cart
     */
    class CoreAddToCartMixin extends AddToCartMixin {
        ACTION_BUTTON_TYPES = {
            ADD_TO_CART: 'addToCart',
            BACK_IN_STOCK: 'backInStock',
            SIGN_IN_TO_BUY: 'signInToBuy'
        } as const;

        private currentActionButtonType: TActionButtonType = this.ACTION_BUTTON_TYPES.ADD_TO_CART;

        prefs() {
            return {
                pageContext: '',
                bopis: {
                    storeId: ''
                },
                textOutOfStockMessage: '',
                expressCheckoutContainerId: 'expressCheckout',
                signInToBuyBtn: 'signInToBuyButton',
                findSimilarBtn: 'findSimilarBtn',
                persistentExpressPayments: false,
                isMembersOnly: false,
                isLoyaltyEnrollmentRequired: false,
                isAvailable: false,
                selectSizeLabel: '',
                enableDynamicSizeCta: false,
                findSimilarProductsEnable: false,
                attributeSelections: <{ [key: string]: boolean }>{},
                ...super.prefs(),
                showAlertOnProductAdd: false
            };
        }

        /**
         * @description Stub method for getting selected delivery data
         */
        getSelectedDeliveryData(): TDeliveryMethodData {
            return {
                isBOPIS: false
            };
        }

        /**
         * @description Widget initialization
         */
        init(): void {
            super.init();

            this.initActionButtons();
            this.initExpressCheckoutEvents();
            this.initFindSimilarButton();
            this.observeRecommendationsSlot();
        }

        /**
         * @description Initializes action buttons
         */
        initActionButtons() {
            this.getById(this.prefs().addToCartBtn, (addToCartBtn: TProcessButtonInstance) => {
                const { backInStockSubscriptionEnable, isAvailable, isMembersOnly, isLoyaltyEnrollmentRequired } = this.prefs();
                const isSignInToBuy = isAvailable && isMembersOnly && !isMember(isLoyaltyEnrollmentRequired);

                let actionButtonType: TActionButtonType = this.ACTION_BUTTON_TYPES.ADD_TO_CART;

                if (backInStockSubscriptionEnable) {
                    actionButtonType = this.ACTION_BUTTON_TYPES.BACK_IN_STOCK;
                } else if (isSignInToBuy) {
                    actionButtonType = this.ACTION_BUTTON_TYPES.SIGN_IN_TO_BUY;
                }

                this.coreToggleActionButtons(addToCartBtn, actionButtonType);
            });
        }

        /**
         * @description Handles check submit add to cart event
         * @param widget initiator widget
         * @param callback callback function
         */
        handleCheckSubmitAddToCart(_: TWidget, callback: (self: this, result: { canSubmit: boolean }) => void) {
            callback(this, { canSubmit: this.canSubmitAddToCart() });
        }

        /**
         * @description Checks if add to cart button can be submitted
         */
        private canSubmitAddToCart(): boolean {
            const prefs = this.prefs();

            if (this.invalidBundleItems?.length) {
                this.showBundleErrors();

                return false;
            }

            if (this.callIfExists('checkUnselectedAttributes') === false) {
                return false;
            }

            if (!prefs.readyToOrder) {
                this.showCartMessage(prefs.textSelectOptions, true);

                return false;
            }

            if (this.product.stock && this.product.stock < prefs.selectedQuantity) {
                this.showCartMessage(prefs.textStockLimit, true);

                return false;
            }

            const selectedOptions = this.getSelectedProductOptions();
            const isSelectedOptionsValid = this.validateProductOptions(selectedOptions);

            if (!isSelectedOptionsValid) {
                return false;
            }

            return true;
        }

        /**
         * @description Builds request data for add to cart request
         * @param button Add Product to Cart button
         */
        buildAddToCartRequestData(button: TProcessButtonInstance): TAddProductRequestData {
            const childProducts = this.prepareBundleItems();
            const selectedOptions = this.getSelectedProductOptions();
            const prefs = this.prefs();
            const addToCartBtnPrefs = button.prefs();

            const requestData: TAddProductRequestData = {
                pid: prefs.currentProductId || addToCartBtnPrefs.pid,
                quantity: this.getSelectedQuantity() || addToCartBtnPrefs.selectedQuantity,
                pageContext: prefs.pageContext
            };

            const selectedDeliveryData = this.getSelectedDeliveryData();

            if (selectedDeliveryData.isBOPIS) {
                requestData.storeId = selectedDeliveryData.storeId || prefs.bopis.storeId;
            }

            if (selectedOptions.length) {
                requestData.options = JSON.stringify(selectedOptions);
            }

            if (childProducts.length) {
                requestData.childProducts = JSON.stringify(childProducts);
            }

            return requestData;
        }

        /**
         * @description Update Product View
         * @param product productModel
         */
        updateProductView(product: TProduct): void {
            super.updateProductView(product);
            this.setPref('attributeSelections', product.attributeSelections);
            this.callIfExists('highlightUnselectedAttributes');
        }

        /**
         * @description Handles add to cart button click
         * @param button Add Product response
         * @param onBeforeSubmit Callback that executes before the request is submitted
         * @returns Promise object represents server response for session continuation
         */
        addToCart(button: TAddToCartButtonInstance, onBeforeSubmit?: () => void): Promise<unknown | null> {
            const prefs = this.prefs();

            if (button.config?.analytics) {
                this.currentGtmInfo = button.config.analytics as TGTMInfo;
            }

            if (button.prefs().isOutOfStock) {
                this.showCartMessage(prefs.textOutOfStockMessage, true);

                return Promise.resolve(null);
            }

            if (prefs.processingRequest || !this.canSubmitAddToCart()) {
                return Promise.resolve(null);
            }

            this.setPref('processingRequest', true);
            button.startProcess();
            this.showProgressBar();

            const requestData = this.buildAddToCartRequestData(button);

            if (typeof onBeforeSubmit === 'function') {
                onBeforeSubmit();
            }

            return submitFormJson(button.prefs().addToCartUrl, requestData)
                .then(response => {
                    this.setPref('processingRequest', false);
                    button.stopProcess();
                    this.hideProgressBar();

                    this.postAddProduct(response);

                    return response;
                })
                .catch(() => {
                    this.setPref('processingRequest', false);
                    button.stopProcess();
                    this.hideProgressBar();

                    this.showCartMessage(prefs.textNetworkError, true);
                });
        }

        /**
         * @description Handles response from server once product added to cart
         * @emits "productadded"
         * @emits "minicart.need.update"
         * @emits "product.added.to.cart"
         * @param response Add Product response
         */
        postAddProduct(response: TAddToCartServerResponse) {
            if (response.success && response.cart) {
                this.emit('productadded', this);
                const cartModel = response.cart;

                cartModel.showMinicart = this.prefs().showMinicartOnProductAdd;

                if (cartModel.addedBonusProductsGtmInfo) {
                    this.bonusGtmInfo = cartModel.addedBonusProductsGtmInfo;
                }

                if (response.addToCartOverlayUrl) {
                    const analyticsOnCancel = this.getAnalyticsOnCancel(response);

                    this.eventBus().emit('drawer.cancel');
                    this.eventBus().emit('drawershow', {
                        url: response.addToCartOverlayUrl,
                        headerText: response.addToCartOverlayHeader,
                        ariaLabel: response.addToCartOverlayHeader,
                        bodyClass: 'm-has_dialog',
                        wrapperClasses: 'm-cart_overlay',
                        analyticsOnCancel: analyticsOnCancel
                    });
                }

                this.eventBus().emit('minicart.need.update', cartModel, this);
                this.eventBus().emit('minicart.updated', cartModel);
                this.eventBus().emit('product.added.to.cart', cartModel, this);
                this.eventBus().emit('bonus.product.added.to.cart', cartModel, this);
                this.eventBus().emit('ESP.product.added.to.cart', response.ESPAddToCartData, this);

                if (this.prefs().showAlertOnProductAdd && this.prefs().accessibilityAlerts) {
                    const accessibilityAlert = this.prefs().accessibilityAlerts.addedtocart;

                    this.eventBus().emit('alert.show', {
                        accessibilityAlert
                    });
                }
            }

            if (!response.success || this.prefs().showMessageOnProductAdd) {
                this.eventBus().emit('product.addedTocart.with.error', response);
                this.showCartMessage(response.errorMessage, !response.success);
            }
        }

        /**
         * @description Gets analytics data for the close overlay
         * @param response Server response from add to cart action
         * @returns Analytics data object or null if no item ID is found
         */
        getAnalyticsOnCancel(response: TAddToCartServerResponse) {
            let itemId;

            if (response.cart?.addedProducts?.length) {
                response.cart?.addedProducts
                    ?.reduce((arr: Array<string>, item: { gtmInfo?: { item_id?: string } }) => {
                        const id = item?.gtmInfo?.item_id;

                        if (id) {
                            arr.push(id);
                        }

                        return arr;
                    }, [])
                    .join(';');
            } else {
                itemId = response.cart?.addedProduct?.gtmInfo?.item_id;
            }

            if (!itemId) {
                return null;
            }

            return {
                event_name: 'close_overlay',
                close_overlay: {
                    feature: 'atc_overlay',
                    item_id: itemId
                }
            };
        }

        /**
         * @description Renders message to add to cart container (or bundle item container) and shows it
         * @param msg Message
         * @param error Error flag
         * @param bundleItemMsg Bundle item widget
         */
        showCartMessage(msg: string, error?: boolean, bundleItemMsg?) {
            this.getById(this.prefs().addToCartMsg, addToCartMsg => {
                if (typeof this.spacesLength === 'undefined') {
                    this.spacesLength = 0;
                }

                // It's needed to force render previously added success message. If we try to add the same product
                // several times, success message alert will not be announced by a screen reader.
                msg = msg.concat(Array(++this.spacesLength).join(this.prefs().zeroWidthSpaceSymbol));

                addToCartMsg
                    .render('template', { msg, error }, bundleItemMsg ? bundleItemMsg.ref('container') : addToCartMsg.ref('container'))
                    .then(() => {
                        if (bundleItemMsg) {
                            bundleItemMsg.show();
                        } else {
                            addToCartMsg.show();
                        }
                    });
            });
        }

        /**
         * @description Shows the message when the quantity limit for a product is reached.
         * @param message The message to display.
         */
        private showReachedLimitMessage(message: string) {
            this.getById(this.prefs().addToCartMsg, addToCartMsg => {
                const error = true;

                addToCartMsg.render('template', { msg: message, error }, addToCartMsg.ref('container')).then(() => addToCartMsg.show());
            });
        }

        /**
         * @description Allows to toggle action buttons on PDP based on the received button type
         * @param addToCartBtn Add to cart button
         * @param buttonType Type of button to toggle
         */
        coreToggleActionButtons(addToCartBtn: TProcessButtonInstance, buttonType: TActionButtonType = this.ACTION_BUTTON_TYPES.ADD_TO_CART) {
            const backInStockBtn = this.ref(this.prefs().backInStockBtn);
            const signInToBuyBtn = this.ref(this.prefs().signInToBuyBtn);

            this.currentActionButtonType = buttonType;

            switch (buttonType) {
                case this.ACTION_BUTTON_TYPES.BACK_IN_STOCK:
                    addToCartBtn.hide();
                    signInToBuyBtn.hide();
                    backInStockBtn.show();
                    break;

                case this.ACTION_BUTTON_TYPES.SIGN_IN_TO_BUY:
                    addToCartBtn.hide();
                    signInToBuyBtn.show();
                    backInStockBtn.hide();
                    break;

                default:
                    addToCartBtn.show();
                    signInToBuyBtn.hide();
                    backInStockBtn.hide();
            }

            this.updateFindSimilarButtonVisibility();
        }

        /**
         * @description Handles the UI update for an out-of-stock product.
         * @param addToCartBtn The 'Add to Cart' button instance.
         * @param product The product data.
         */
        private handleOutOfStock(addToCartBtn: IAddToCartButton, product: TProduct) {
            const actionButtonType = product.backInStockSubscription?.isEnabled
                ? this.ACTION_BUTTON_TYPES.BACK_IN_STOCK
                : this.ACTION_BUTTON_TYPES.ADD_TO_CART;

            addToCartBtn.setPref('isOutOfStock', true);
            addToCartBtn.setText(this.prefs().outOfStockLabel);
            addToCartBtn.disable();
            this.coreToggleActionButtons(addToCartBtn, actionButtonType);
        }

        /**
         * @description Handles the UI update when the product quantity limit is reached.
         * @param addToCartBtn The 'Add to Cart' button instance.
         * @param product The product data.
         */
        private handleReachedLimit(addToCartBtn: IAddToCartButton, product: TProduct) {
            this.showReachedLimitMessage(product.availability.isReachedLimitMessage as string);
            addToCartBtn.setPref('isOutOfStock', true);
            addToCartBtn.setText(this.prefs()[this.prefs().update ? 'updateLabel' : 'addToCartLabel']);
            addToCartBtn.disable();
            this.coreToggleActionButtons(addToCartBtn);
        }

        /**
         * @description Handles the UI update for a product that requires sign in to purchase.
         * @param addToCartBtn The 'Add to Cart' button instance.
         */
        private handleSignInToBuy(addToCartBtn: IAddToCartButton) {
            addToCartBtn.setPref('isOutOfStock', false);
            addToCartBtn.disable();
            this.coreToggleActionButtons(addToCartBtn, this.ACTION_BUTTON_TYPES.SIGN_IN_TO_BUY);
        }

        /**
         * @description Handles the UI update for an available product.
         * @param addToCartBtn The 'Add to Cart' button instance.
         * @param product The product data.
         */
        private handleAvailable(addToCartBtn: IAddToCartButton, product?: TProduct) {
            addToCartBtn.setPref('isOutOfStock', false);
            addToCartBtn.setText(
                this.shouldShowSelectSize(product)
                    ? this.prefs().selectSizeLabel
                    : this.prefs()[this.prefs().update ? 'updateLabel' : 'addToCartLabel']
            );
            addToCartBtn.enable();
            this.coreToggleActionButtons(addToCartBtn);
        }

        /**
         * @description Whether the add to cart CTA should show the "select size" label instead of "add to cart".
         * True only when the dynamic size CTA is enabled for the country and the size attribute is not yet selected.
         * @param product The product data used to read the fresh attribute selections.
         */
        shouldShowSelectSize(product?: TProduct): boolean {
            if (!this.prefs().enableDynamicSizeCta) {
                return false;
            }

            const selections = (product && product.attributeSelections) || this.prefs().attributeSelections || {};

            return Object.prototype.hasOwnProperty.call(selections, 'size') && selections.size === false;
        }

        /**
         * @description Render Add to Cart button
         * @param product productModel
         */
        renderAddToCart(product: TProduct) {
            const prefs = this.prefs();
            const isSignInToBuy = product.membersOnly && !isMember(prefs.isLoyaltyEnrollmentRequired);

            this.getById<IAddToCartButton>(prefs.addToCartBtn, addToCartBtn => {
                if (!product.available && product.availability.isOutOfStock && !product.isInaccessibleVariant) {
                    this.handleOutOfStock(addToCartBtn, product);
                } else if (product.availability.isReachedLimit) {
                    this.handleReachedLimit(addToCartBtn, product);
                } else if (product.available && isSignInToBuy && this.prefs().pageContext !== 'wishlist') {
                    this.handleSignInToBuy(addToCartBtn);
                } else {
                    this.handleAvailable(addToCartBtn, product);
                }
            });
        }

        /**
         * @description Handles express payment buttons container click event
         * @param event Event object
         */
        handleExpressPaymentClick(event: Event) {
            // Prevent proceeding to payment if add to cart cannot be submitted
            if (!this.canSubmitAddToCart()) {
                // Stop event propagation to avoid triggering payment process
                event.stopPropagation();
                event.preventDefault();
            }
        }

        /**
         * @description Re-checks find-similar button visibility once the recommendations slot content changes.
         * The slot can be filled asynchronously (e.g. Einstein/CQuotient recommendations fetched via XHR with no
         * completion event) or reloaded on color change, so visibility can't be decided once at toggle time alone.
         */
        private observeRecommendationsSlot() {
            const slotContainer = document.querySelector(RECOMMENDATIONS_SLOT_SELECTOR);

            if (!slotContainer) {
                return;
            }

            const observer = new MutationObserver(() => this.updateFindSimilarButtonVisibility());

            observer.observe(slotContainer, { childList: true, subtree: true });

            this.onDestroy(() => observer.disconnect());
        }

        /**
         * @description Shows/hides the find-similar button based on the current action button state and
         * whether the pdp-bottom-1 recommendations slot currently has content
         */
        private updateFindSimilarButtonVisibility() {
            const findSimilarBtn = this.ref(this.prefs().findSimilarBtn);

            if (
                this.currentActionButtonType === this.ACTION_BUTTON_TYPES.BACK_IN_STOCK &&
                this.prefs().findSimilarProductsEnable &&
                !this.isRecommendationsSlotEmpty()
            ) {
                findSimilarBtn.show();
            } else {
                findSimilarBtn.hide();
            }
        }

        /**
         * @description Whether the pdp-bottom-1 recommendations slot has no rendered product tiles.
         * Checks specifically for carousel tiles (same markup as productsCarousel.isml/recentlyViewedCarousel.isml)
         * rather than any media/text, so a plain banner slot isn't mistaken for a recommendations carousel.
         */
        private isRecommendationsSlotEmpty(): boolean {
            const slotContainer = document.querySelector(RECOMMENDATIONS_SLOT_SELECTOR);

            if (!slotContainer) {
                return true;
            }

            return slotContainer.querySelectorAll('.b-carousel-item, [data-tau="carousel_tile"]').length === 0;
        }

        /**
         * @description Handles Find Similar button click
         */
        handleFindSimilarClick(event: Event) {
            const target = event.target as HTMLElement;

            if (!target.closest('.m-find_similar')) {
                return;
            }

            const similarSection = document.querySelector(RECOMMENDATIONS_SLOT_SELECTOR);
            const header = document.querySelector('.l-header');

            if (!similarSection) {
                return;
            }

            const headerHeight = header?.getBoundingClientRect().height || 0;
            const elementPosition = similarSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - headerHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }

        /**
         * @description Initializes Find Similar button click event
         */
        initFindSimilarButton() {
            const el = this.refs.self.get();

            if (!el) {
                return;
            }

            const handler = this.handleFindSimilarClick.bind(this);

            el.addEventListener('click', handler);

            this.onDestroy(() => {
                el.removeEventListener('click', handler);
            });
        }

        /**
         * @description Initializes express checkout events
         */
        initExpressCheckoutEvents() {
            const prefs = this.prefs();

            if (!prefs.persistentExpressPayments) {
                return;
            }

            const el = this.refs.self.get();
            const expressCheckoutContainer = el?.querySelector('#' + prefs.expressCheckoutContainerId);

            if (!expressCheckoutContainer) {
                return;
            }

            const handler = this.handleExpressPaymentClick.bind(this);
            const useCapture = true;

            expressCheckoutContainer.addEventListener('click', handler, useCapture);

            this.onDestroy(() => {
                expressCheckoutContainer.removeEventListener('click', handler, useCapture);
            });
        }
    }

    return CoreAddToCartMixin;
}

export type TCoreAddToCartMixin = ReturnType<typeof CoreAddToCartMixinExtClassCreator>;

export type TCoreAddToCartMixinInstance = InstanceType<TCoreAddToCartMixin>;

export default CoreAddToCartMixinExtClassCreator;
